import axios from 'axios';

import { getAPIUrl } from './baseAPI';
import { queryStringify } from 'util/api';
import { QuestionQuotaBody, QuestionQuotaResult } from 'types/domainModels';

export const FETCH_QUESTION_QUOTAS = ({ surveyId }: { surveyId: number }) => ({
  path: `/surveys/${surveyId}/question-quota-results`,
  version: 'v1' as const,
});

export async function fetchQuestionQuotas({
  surveyId,
  waveIds,
}: {
  surveyId: number;
  waveIds: number[];
}) {
  return (
    await axios.get<QuestionQuotaResult[]>(
      getAPIUrl(FETCH_QUESTION_QUOTAS({ surveyId })),
      {
        params: { waveIds: waveIds.length > 0 ? waveIds : undefined },
        // The default query param format for the waveIds array is waveIds[]=1&waveIds[]=2,
        // but the backend expects waveIds=1&waveIds=2.
        paramsSerializer: queryStringify,
      },
    )
  ).data;
}

export const REMOVE_QUESTION_QUOTAS = ({
  questionId,
}: {
  questionId: number;
}) => ({
  path: `/questions/${questionId}/quotas`,
  version: 'v1' as const,
});

export async function removeQuestionQuotas({
  questionId,
  waveIds,
}: {
  questionId: number;
  waveIds: number[];
}) {
  return (
    await axios.delete<void>(
      getAPIUrl(REMOVE_QUESTION_QUOTAS({ questionId })),
      {
        params: { waveIds: waveIds.length > 0 ? waveIds : undefined },
        // The default query param format for the waveIds array is waveIds[]=1&waveIds[]=2,
        // but the backend expects waveIds=1&waveIds=2.
        paramsSerializer: queryStringify,
      },
    )
  ).data;
}

export const CREATE_QUESTION_QUOTA = () => ({
  path: '/questionQuotas',
  version: 'v2' as const,
});

export async function createQuestionQuota({
  data,
}: {
  data: QuestionQuotaBody;
}) {
  return (
    await axios.post<QuestionQuotaBody & { id: number }>(
      getAPIUrl(CREATE_QUESTION_QUOTA()),
      data,
    )
  ).data;
}

export const CREATE_QUESTION_QUOTAS_BULK = () => ({
  path: '/questionQuotas/bulk',
  version: 'v2' as const,
});

export async function createQuestionQuotasBulk({
  data,
}: {
  data: QuestionQuotaBody[];
}) {
  return (
    await axios.post<(QuestionQuotaBody & { id: number })[]>(
      getAPIUrl(CREATE_QUESTION_QUOTAS_BULK()),
      data,
    )
  ).data;
}

export const FETCH_QUESTION_QUOTA = ({
  questionQuotaId,
}: {
  questionQuotaId: number;
}) => ({
  path: `/questionQuotas/${questionQuotaId}`,
  version: 'v2' as const,
});

export async function fetchQuestionQuota({
  questionQuotaId,
}: {
  questionQuotaId: number;
}) {
  return (
    await axios.get<QuestionQuotaBody & { id: number }>(
      getAPIUrl(FETCH_QUESTION_QUOTA({ questionQuotaId })),
    )
  ).data;
}

export const UPDATE_QUESTION_QUOTA = ({
  questionQuotaId,
}: {
  questionQuotaId: number;
}) => ({
  path: `/questionQuotas/${questionQuotaId}`,
  version: 'v2' as const,
});

export async function updateQuestionQuota({
  questionQuotaId,
  data,
}: {
  questionQuotaId: number;
  data: Partial<QuestionQuotaBody>;
}) {
  return (
    await axios.patch<QuestionQuotaBody & { id: number }>(
      getAPIUrl(UPDATE_QUESTION_QUOTA({ questionQuotaId })),
      data,
    )
  ).data;
}

export const DELETE_QUESTION_QUOTA = ({
  questionQuotaId,
}: {
  questionQuotaId: number;
}) => ({
  path: `/questionQuotas/${questionQuotaId}`,
  version: 'v2' as const,
});

export async function deleteQuestionQuota({
  questionQuotaId,
}: {
  questionQuotaId: number;
}) {
  return (
    await axios.delete(getAPIUrl(DELETE_QUESTION_QUOTA({ questionQuotaId })))
  ).data;
}
